import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import styles from "./NotificationChannels.module.scss";
import ToggleSwitch from "@/components/ToggleSwitch/ToggleSwitch";
import SettingsService from "@/services/api/settings";
import toast from "@/lib/toast";
import type { NotificationChannelItem } from "@/types/settings";

export interface NotificationChannelsHandle {
  save: () => Promise<void>;
  cancel: () => void;
}

export interface NotificationChannelsProps {
  onSavingChange?: (isSaving: boolean) => void;
}

const NotificationChannels = forwardRef<NotificationChannelsHandle, NotificationChannelsProps>(
  ({ onSavingChange }, ref) => {
    const [channels, setChannels] = useState<NotificationChannelItem[]>([]);
    const [savedChannels, setSavedChannels] = useState<NotificationChannelItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const fetchChannels = useCallback(async () => {
      setIsLoading(true);
      try {
        const data = await SettingsService.getNotificationSettings();
        setChannels(data);
        setSavedChannels(data);
      } catch {
        // privateRequest's response interceptor already shows an error toast
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchChannels();
    }, [fetchChannels]);

    const handleToggle = (id: number, field: "emailEnabled" | "pushEnabled") => {
      if (isSaving) return;
      setChannels((prev) =>
        prev.map((item) => (item.id === id ? { ...item, [field]: !item[field] } : item)),
      );
    };

    const save = useCallback(async () => {
      if (isSaving) return;

      const hasChanges = channels.some((item) => {
        const original = savedChannels.find((saved) => saved.id === item.id);
        return !original
          || original.emailEnabled !== item.emailEnabled
          || original.pushEnabled !== item.pushEnabled;
      });

      if (!hasChanges) return;

      setIsSaving(true);
      onSavingChange?.(true);
      try {
        await SettingsService.updateNotificationChannel(channels);
        toast.success("Notification settings updated successfully");
        await fetchChannels();
      } catch {
        // privateRequest's response interceptor already shows an error toast
      } finally {
        setIsSaving(false);
        onSavingChange?.(false);
      }
    }, [channels, savedChannels, isSaving, onSavingChange, fetchChannels]);

    const cancel = useCallback(() => {
      setChannels(savedChannels);
    }, [savedChannels]);

    useImperativeHandle(ref, () => ({ save, cancel }), [save, cancel]);

    return (
      <div className={styles.channelsCard}>
        <h2 className={styles.cardTitle}>Notification channels</h2>

        {isLoading ? (
          <p className={styles.loadingText}>Loading notification channels...</p>
        ) : (
          <div className={styles.channelList}>
            <div className={styles.channelHeader}>
              <span className={styles.headerLabel}>Channel type &amp; trigger</span>
              <span className={styles.headerToggle}>Email</span>
              <span className={styles.headerToggle}>Push</span>
            </div>

            {channels.map((channel) => (
              <div key={channel.id} className={styles.channelRow}>
                <div className={styles.channelInfo}>
                  <span className={styles.channelTitle}>{channel.title}</span>
                  <span className={styles.channelDescription}>{channel.description}</span>
                </div>

                <div className={styles.toggleCell}>
                  <ToggleSwitch
                    checked={channel?.emailEnabled}
                    onChange={() => handleToggle(channel.id, "emailEnabled")}
                    aria-label={`${channel.title} — Email`}
                    disabled={isSaving}
                  />
                </div>

                <div className={styles.toggleCell}>
                  <ToggleSwitch
                    checked={channel?.pushEnabled}
                    onChange={() => handleToggle(channel.id, "pushEnabled")}
                    aria-label={`${channel.title} — Push`}
                    disabled={isSaving}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

NotificationChannels.displayName = "NotificationChannels";

export default NotificationChannels;
